import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Box,
  MapPin,
  ScanLine,
  Boxes,
  Wrench,
  ShieldAlert,
  Cpu,
  FolderTree,
  Settings,
  ChevronLeft,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAppStore } from "../../stores/appStore";
import { useSiteSelection } from "./OrgSiteSelector";

const navItems: Array<{
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  wip?: boolean;
  /** Whether this destination requires a virtual site to be picked. Settings
   *  stays enabled so the user always has somewhere to go while gated. */
  requiresSite?: boolean;
}> = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", requiresSite: true },
  { to: "/assets", icon: Box, label: "Asset Register", requiresSite: true },
  { to: "/point-clouds", icon: ScanLine, label: "Point Clouds", requiresSite: true },
  { to: "/digital-twins", icon: Boxes, label: "Digital Twins", requiresSite: true },
  { to: "/maintenance", icon: Wrench, label: "Maintenance", requiresSite: true },
  { to: "/risk", icon: ShieldAlert, label: "Risk", wip: true, requiresSite: true },
  { to: "/instrumentation", icon: Cpu, label: "Instrumentation", wip: false, requiresSite: true },
  { to: "/dms", icon: FolderTree, label: "Documents", requiresSite: true },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export function SideNav() {
  const { sidebarCollapsed, toggleSidebar } = useAppStore();
  const { needsSiteSelection } = useSiteSelection();

  return (
    <motion.aside
      initial={false}
      animate={{ width: sidebarCollapsed ? 72 : 256 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-white/10 bg-surface-900/40 backdrop-blur-2xl backdrop-saturate-150 shadow-[1px_0_0_rgba(255,255,255,0.04)_inset,8px_0_32px_rgba(2,6,23,0.35)]"
    >
      <div
        className={cn(
          "flex h-16 items-center border-b border-white/10 px-4",
          sidebarCollapsed ? "justify-center" : "justify-between"
        )}
      >
        <NavLink to="/" className="flex items-center gap-2.5">
          <div className="relative h-9 w-9">
            {/* Subtle pulse halo behind the logo. The logo PNG sits above it
                via z-10 so the gradient never tints the artwork itself. */}
            <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-brand-500 to-purple-500 animate-ping opacity-20" />
            <img
              src="/files/favicon-192.png"
              alt="Asset Radar logo"
              className="relative z-10 h-9 w-9 rounded-lg shadow-lg shadow-brand-500/20"
            />
          </div>
          {!sidebarCollapsed && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-lg font-bold text-surface-100"
            >
              Asset<span className="text-gradient">Radar</span>
            </motion.span>
          )}
        </NavLink>

        {!sidebarCollapsed && (
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-surface-400 hover:bg-white/10 hover:text-surface-100 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {navItems.map((item) => {
          const disabled = needsSiteSelection && !!item.requiresSite;
          const className = ({ isActive }: { isActive: boolean }) =>
            cn(
              "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
              sidebarCollapsed && "justify-center px-2",
              disabled
                ? "text-surface-500 border border-transparent cursor-not-allowed opacity-50"
                : isActive
                  // Use surface-50 for the label — it inverts to dark-brown
                  // automatically in light mode (see index.css overrides) so
                  // the active row reads in both themes. Gradient is now
                  // indigo-only (purple drop) and the colored halo is gone.
                  ? "bg-gradient-to-r from-brand-500/20 to-brand-500/10 text-surface-50 border border-white/15 shadow-[0_1px_0_rgba(255,255,255,0.08)_inset] backdrop-blur-md"
                  : "text-surface-300 hover:bg-white/[0.06] hover:text-surface-50 border border-transparent hover:border-white/10"
            );
          const renderInner = ({ isActive }: { isActive: boolean }) => (
            <>
              {isActive && !disabled && (
                <motion.div
                  layoutId="activeNavIndicator"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-gradient-to-b from-brand-400 to-purple-400"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}
              <item.icon
                className={cn(
                  "h-5 w-5 shrink-0 transition-colors",
                  disabled
                    ? "text-surface-600"
                    : isActive
                      ? "text-brand-300"
                      : "text-surface-500 group-hover:text-surface-300"
                )}
              />
              {!sidebarCollapsed && (
                <span className="flex flex-1 items-center gap-2">
                  <span>{item.label}</span>
                  {item.wip && (
                    <span
                      className="rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-300"
                      title="Work in progress"
                    >
                      WIP
                    </span>
                  )}
                </span>
              )}
              {sidebarCollapsed && item.wip && (
                <span
                  className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(245,158,11,0.7)]"
                  title="Work in progress"
                />
              )}
            </>
          );

          if (disabled) {
            return (
              <div
                key={item.to}
                title="Select a virtual site to continue"
                aria-disabled="true"
                className={className({ isActive: false })}
              >
                {renderInner({ isActive: false })}
              </div>
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={className}
            >
              {renderInner}
            </NavLink>
          );
        })}
      </nav>

      {sidebarCollapsed && (
        <div className="border-t border-white/10 p-2">
          <button
            onClick={toggleSidebar}
            className="flex w-full items-center justify-center rounded-lg p-2 text-surface-400 hover:bg-white/10 hover:text-surface-100 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 rotate-180" />
          </button>
        </div>
      )}

      <div
        className={cn(
          "border-t border-white/10 p-3",
          sidebarCollapsed && "hidden"
        )}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <MapPin className="h-4 w-4 text-surface-500" />
            <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <span className="text-xs text-surface-500">
            Asset Radar v{__APP_VERSION__}
          </span>
        </div>
      </div>
    </motion.aside>
  );
}
